import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../api';
import Drawer from '../UI/Drawer';
import { primaryBtn, primaryBtnStyle, ghostBtn, ghostBtnStyle, inputCls, inputStyle, errMessage } from '../UI/ink';
import { NEW_TYPES, agentBand, agoLong, explain } from './model';

const BAND = {
  ok: ['Reporting', 'var(--sev-good-fg)', 'transparent'],
  late: ['Late', 'var(--sev-med-fg)', 'transparent'],
  silent: ['Silent · would wait', 'transparent', 'var(--ink-faint)'],
  never: ['Never checked in · would wait', 'transparent', 'var(--ink-faint)'],
};
const EMPTY = { server_id: '', type: 'block_ip', ip: '', domain: '', pid: '', name: '', path: '', case_id: '', reason: '' };
const mono = { ...inputStyle, fontFamily: "'JetBrains Mono', monospace" };

// One host, one action, the one or two fields it needs, a reason and an
// optional case. Nothing runs until someone confirms it on the page.
export default function NewActionDrawer({ open, onClose, onCreated, me, initialServerId }) {
  const [hosts, setHosts] = useState([]);
  const [cases, setCases] = useState([]);
  const [q, setQ] = useState('');
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY, server_id: initialServerId || '' }); setError(null); setQ('');
    apiClient.get('/api/servers').then(r => setHosts((r.data?.servers || [])
      .filter(s => s.source !== 'arp_passive' && !['DISCOVERED', 'ARCHIVED', 'IGNORED'].includes(s.lifecycle_state))
      .sort((a, b) => String(a.hostname).localeCompare(String(b.hostname))))).catch(() => setHosts([]));
    apiClient.get('/api/cases?status=active&limit=100&sort=updated').then(r => setCases(r.data?.cases || [])).catch(() => setCases([]));
  }, [open, initialServerId]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const host = hosts.find(h => h.id === form.server_id);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = s ? hosts.filter(h => `${h.hostname} ${h.ip_address || ''}`.toLowerCase().includes(s)) : hosts;
    // Keep the chosen host visible while filtering.
    return host && !list.includes(host) ? [host, ...list.slice(0, 7)] : list.slice(0, 8);
  }, [hosts, q, host]);

  const config = form.type === 'block_ip' ? { ip: form.ip.trim() }
    : form.type === 'block_domain' ? { domain: form.domain.trim() }
    : form.type === 'kill_process' ? { pid: Number(form.pid), expected_name: form.name.trim() }
    : form.type === 'quarantine_file' ? { path: form.path.trim() } : {};
  const ready = form.server_id && form.reason.trim() && (
    form.type === 'block_ip' ? form.ip.trim()
      : form.type === 'block_domain' ? form.domain.trim()
      : form.type === 'kill_process' ? /^\d+$/.test(form.pid.trim()) && form.name.trim()
      : form.type === 'quarantine_file' ? form.path.trim() : true);
  const preview = host && ready ? explain({ action_type: form.type, action_config: config, hostname: host.hostname, os_type: host.os_type }) : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true); setError(null);
    try {
      const r = await apiClient.post('/api/actions', {
        server_id: form.server_id, action_type: form.type, action_config: config,
        reason: form.reason.trim(), case_id: form.case_id || null,
      });
      onCreated(r.data?.action);
    } catch (err) {
      const existing = err?.response?.data?.existing_id;
      setError(errMessage(err, 'Could not queue the action.'));
      if (existing) onCreated({ id: existing }, true);
    } finally {
      setBusy(false);
    }
  };

  const field = (label, k, props = {}) => (
    <label className="flex flex-col gap-[5px] text-xs" style={{ color: 'var(--ink-faint)' }}>{label}
      <input type="text" value={form[k]} onChange={set(k)} autoComplete="off" spellCheck={false} className={inputCls} style={mono} {...props} />
    </label>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      kicker="NEW RESPONSE ACTION"
      title="What should happen, and where?"
      subtitle="It waits for confirmation before anything runs."
      footer={<><span className="text-xs" style={{ color: 'var(--ink-faint)' }}>Requested by {me}</span><div className="flex gap-2.5"><button type="button" onClick={onClose} className={ghostBtn} style={ghostBtnStyle}>Cancel</button><button type="submit" form="new-action" disabled={!ready || busy} className={primaryBtn} style={primaryBtnStyle}>{busy ? 'Queuing…' : 'Queue for confirmation'}</button></div></>}
    >
      <form id="new-action" onSubmit={submit} className="flex flex-col gap-[18px]">
        {error && <div role="alert" className="px-3.5 py-2.5 rounded-[10px] border text-[13px]" style={{ borderColor: 'var(--sev-crit-fg)', color: 'var(--sev-crit-fg)', background: 'var(--sev-crit-bg)' }}>{error}</div>}

        <fieldset className="m-0 p-0 border-0 flex flex-col gap-1.5">
          <legend className="pb-2 text-[13px] font-medium">1 · Host</legend>
          {hosts.length > 8 && <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="Find a host or IP" aria-label="Find a host" className={inputCls} style={{ ...inputStyle, height: 40 }} />}
          {!hosts.length && <span className="text-[13px]" style={{ color: 'var(--ink-faint)' }}>No registered hosts with an agent.</span>}
          {shown.map(h => {
            const band = agentBand(h.last_seen);
            const [lbl, dot, dotB] = BAND[band];
            const on = form.server_id === h.id;
            return (
              <label key={h.id} className="flex items-center gap-2.5 px-3 py-[9px] rounded-[9px] border cursor-pointer" style={{ borderColor: on ? 'var(--accent)' : 'var(--surf-line)', background: 'var(--surf-bg)' }}>
                <input type="radio" name="host" checked={on} onChange={() => setForm(f => ({ ...f, server_id: h.id }))} className="m-0 w-[15px] h-[15px]" style={{ accentColor: 'var(--accent)' }} />
                <span className="flex-grow min-w-0 truncate font-mono text-[12.5px]">{h.hostname}<span className="ml-2" style={{ color: 'var(--ink-faint)' }}>{h.ip_address}</span></span>
                <span className="flex items-center gap-1.5 text-xs whitespace-nowrap" style={{ color: 'var(--ink-muted)' }}><span className="w-[7px] h-[7px] box-border rounded border" style={{ background: dot, borderColor: dotB === 'transparent' ? dot : dotB }} />{lbl}{band === 'ok' || band === 'late' ? ` · ${agoLong(h.last_seen)}` : ''}</span>
              </label>
            );
          })}
        </fieldset>

        <fieldset className="m-0 p-0 border-0 flex flex-col gap-1.5">
          <legend className="pb-2 text-[13px] font-medium">2 · Action</legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {NEW_TYPES.map(([id, t, d]) => {
              const on = form.type === id;
              return (
                <label key={id} className="flex items-start gap-2.5 px-3 py-2.5 rounded-[9px] border cursor-pointer" style={{ borderColor: on ? 'var(--accent)' : 'var(--surf-line)', background: 'var(--surf-bg)' }}>
                  <input type="radio" name="atype" checked={on} onChange={() => setForm(f => ({ ...f, type: id }))} className="mt-0.5 w-[15px] h-[15px]" style={{ accentColor: 'var(--accent)' }} />
                  <span className="flex flex-col gap-0.5"><span className="text-[13px] font-medium">{t}</span><span className="text-[11.5px] leading-snug" style={{ color: 'var(--ink-faint)' }}>{d}</span></span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {form.type !== 'isolate_host' && (
          <div className="flex flex-col gap-2">
            <span className="text-[13px] font-medium">3 · Details</span>
            {form.type === 'block_ip' && field('IP address (IPv4 or IPv6)', 'ip', { placeholder: '203.0.113.45' })}
            {form.type === 'block_domain' && field('Domain name', 'domain', { placeholder: 'evil.example.com' })}
            {form.type === 'quarantine_file' && field('Full path of the file', 'path', { placeholder: host?.os_type?.startsWith('win') ? 'C:\\Users\\…\\Downloads\\invoice.exe' : '/tmp/payload' })}
            {form.type === 'kill_process' && (
              <>
                <div className="grid grid-cols-2 gap-2.5">
                  {field('Process ID', 'pid', { inputMode: 'numeric', placeholder: '4121' })}
                  {field('Expected process name', 'name', { placeholder: 'python3' })}
                </div>
                <span className="text-xs leading-snug" style={{ color: 'var(--ink-faint)' }}>
                  The agent only stops PID {form.pid || '…'} if it is still {form.name || 'that program'}. PIDs get reused after a process ends.
                </span>
              </>
            )}
          </div>
        )}
        {form.type === 'isolate_host' && host && (
          <div className="flex flex-col gap-1.5 px-3.5 py-3 rounded-[10px]" style={{ background: 'var(--sev-crit-bg)' }}>
            <span className="text-[13.5px] font-semibold">This cuts {host.hostname} off the network</span>
            <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>
              Everything except DNS and DragonEye stops{host.is_internet_facing ? `. ${host.hostname} is internet-facing, so anything it serves goes down` : ''}. You can release it from this page.
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          <label className="flex flex-col gap-[5px] text-[13px] font-medium">{form.type === 'isolate_host' ? '3' : '4'} · Case <span className="sr-only">(optional)</span>
            <select value={form.case_id} onChange={set('case_id')} className={inputCls} style={{ ...inputStyle, height: 40, fontWeight: 400 }}>
              <option value="">No case</option>
              {cases.map(c => <option key={c.id} value={c.id}>{String(c.id).toUpperCase()} · {c.title}</option>)}
            </select>
          </label>
        </div>
        <label className="flex flex-col gap-1.5 text-[13px] font-medium">{form.type === 'isolate_host' ? '4' : '5'} · Reason
          <textarea rows={2} value={form.reason} onChange={set('reason')} placeholder="Why this, why now. Shown to whoever confirms it." className="box-border px-[11px] py-[9px] rounded-[9px] border text-[13px] font-normal resize-y outline-none focus:border-[color:var(--accent)]" style={inputStyle} />
        </label>

        {preview && (
          <div className="flex flex-col gap-1.5 px-3.5 py-3 rounded-[10px]" style={{ background: 'var(--surf-raised)' }}>
            <span className="font-mono text-[10px] tracking-[0.12em]" style={{ color: 'var(--ink-faint)' }}>THE AGENT ON {String(host.hostname).toUpperCase()} WILL RUN</span>
            <code className="font-mono text-xs leading-relaxed whitespace-pre-wrap break-all">{preview.cmd}</code>
          </div>
        )}
      </form>
    </Drawer>
  );
}
